/**
 * Phase 8 T-06 §7 H — Smart Match canvas context rail.
 *
 * Right column of the three-column canvas. Bound to the currently focused
 * `MatchResult`. Renders identity + contact icons + LinkedIn / location /
 * skills / experience / education blocks, plus a sticky `Ask about this
 * candidate…` input at the bottom that posts to the same orchestrator the
 * left column uses (see `AgentThreadColumn.sendPrompt`).
 */

import { useMemo, useState } from 'react';
import {
  Copy,
  Share2,
  MessageSquare,
  Linkedin,
  MapPin,
  Briefcase,
  GraduationCap,
  Send,
  Sparkles,
  ExternalLink,
  X,
} from 'lucide-react';
import type { MatchResult } from '../types';

interface CandidateContextRailProps {
  match: MatchResult | null;
  onAsk: (text: string) => void;
  onOpenFullPage: (m: MatchResult) => void;
  /**
   * Optional close handler (post user-feedback, 21-Apr). When provided
   * a small `X` button is rendered in the rail header; clicking it
   * clears the focused candidate so the parent can drop this column
   * from the canvas grid and reflow the card stack. Pass `undefined`
   * to hide the button (the rail is then only dismissed by picking a
   * different card or by a parent-owned control).
   */
  onClose?: () => void;
}

type ExperienceEntry = {
  company: string;
  role: string;
  period?: string;
  bullets?: string[];
};

function readExperience(match: MatchResult): ExperienceEntry[] {
  const profile = match.candidate?.profile_structured_json;
  if (!profile || typeof profile !== 'object') return [];
  const record = profile as Record<string, unknown>;
  const raw = record.experience_entries ?? record.experiences ?? record.work_history;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((entry): ExperienceEntry | null => {
      if (!entry || typeof entry !== 'object') return null;
      const e = entry as Record<string, unknown>;
      const company = typeof e.company === 'string' ? e.company : null;
      const role = typeof e.role === 'string' ? e.role : typeof e.title === 'string' ? e.title : null;
      if (!company || !role) return null;
      const period =
        typeof e.period === 'string'
          ? e.period
          : typeof e.duration === 'string'
            ? e.duration
            : typeof e.dates === 'string'
              ? e.dates
              : undefined;
      const bullets = Array.isArray(e.bullets)
        ? (e.bullets.filter((b) => typeof b === 'string') as string[])
        : Array.isArray(e.highlights)
          ? (e.highlights.filter((b) => typeof b === 'string') as string[])
          : undefined;
      return { company, role, period, bullets };
    })
    .filter((x): x is ExperienceEntry => x != null);
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function EmptyRail() {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
      <div
        className="flex h-10 w-10 items-center justify-center rounded-full"
        style={{ background: 'var(--ggj-accent-soft,#D1FAE5)', color: '#065F46' }}
        aria-hidden
      >
        <Sparkles className="h-5 w-5" />
      </div>
      <p
        className="text-[13px] font-medium"
        style={{ color: 'var(--vscode-text-primary)' }}
      >
        Pick a candidate to see context.
      </p>
      <p
        className="text-[12px] leading-relaxed"
        style={{ color: 'var(--vscode-text-secondary)' }}
      >
        Click any match card, or press <kbd className="rounded border px-1">j</kbd>{' '}
        <kbd className="rounded border px-1">k</kbd> to move through the stack.
      </p>
    </div>
  );
}

export function CandidateContextRail({
  match,
  onAsk,
  onOpenFullPage,
  onClose,
}: CandidateContextRailProps) {
  const [showAllSkills, setShowAllSkills] = useState(false);
  const [askDraft, setAskDraft] = useState('');

  const experience = useMemo(() => (match ? readExperience(match) : []), [match]);
  const skills = useMemo(() => match?.candidate?.parsed_skills ?? [], [match]);
  const visibleSkills = showAllSkills ? skills : skills.slice(0, 10);

  if (!match) {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        <EmptyRail />
      </div>
    );
  }

  const candidate = match.candidate;
  const name = candidate?.full_name ?? 'Unknown candidate';
  const role = candidate?.current_title ?? '—';
  const company = candidate?.current_company;
  const location = candidate?.location;
  const linkedin = candidate?.linkedin_url;
  const education = candidate?.education ?? [];

  const copy = (text: string) => {
    try {
      void navigator.clipboard.writeText(text);
    } catch {
      // ignore
    }
  };

  const submitAsk = () => {
    const text = askDraft.trim();
    if (!text) return;
    onAsk(text);
    setAskDraft('');
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Rail header — mirrors the agent column header so both side
          columns share affordances. The close button clears the
          focused candidate in the parent, which drops this column
          from the grid and widens the card stack automatically. */}
      {onClose ? (
        <div
          className="flex h-8 shrink-0 items-center justify-between border-b px-2"
          style={{ borderColor: 'var(--vscode-border-primary)' }}
        >
          <span
            className="px-1 text-[10px] font-semibold uppercase tracking-wider"
            style={{ color: 'var(--vscode-text-tertiary)' }}
          >
            Profile
          </span>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-6 w-6 items-center justify-center rounded-[6px] transition-colors hover:bg-[var(--vscode-bg-hover)]"
            style={{ color: 'var(--vscode-text-tertiary)' }}
            aria-label="Close candidate profile"
            title="Close profile"
          >
            <X className="h-3.5 w-3.5" aria-hidden />
          </button>
        </div>
      ) : null}
      <div
        className="flex-1 overflow-y-auto px-4 py-4"
        role="region"
        aria-label="Candidate context"
      >
        <div className="flex items-start gap-3">
          <div
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-[13px] font-semibold uppercase"
            style={{ background: 'var(--ggj-accent-soft,#D1FAE5)', color: '#065F46' }}
            aria-hidden
          >
            {initials(name)}
          </div>
          <div className="min-w-0 flex-1">
            <p
              className="truncate text-[14px] font-semibold"
              style={{ color: 'var(--vscode-text-primary)' }}
            >
              {name}
            </p>
            <p
              className="truncate text-[12px]"
              style={{ color: 'var(--vscode-text-secondary)' }}
            >
              {role}
              {company ? ` · ${company}` : ''}
            </p>
          </div>
        </div>

        <div className="mt-3 flex items-center gap-1">
          {linkedin ? (
            <a
              href={linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex h-8 w-8 items-center justify-center rounded-[8px] border transition-colors hover:bg-[var(--vscode-bg-hover)]"
              style={{ borderColor: 'var(--vscode-border-primary)', color: 'var(--vscode-text-secondary)' }}
              title="Open LinkedIn"
              aria-label="Open LinkedIn"
            >
              <Linkedin className="h-3.5 w-3.5" aria-hidden />
            </a>
          ) : null}
          {candidate?.email ? (
            <a
              href={`mailto:${candidate.email}`}
              className="inline-flex h-8 w-8 items-center justify-center rounded-[8px] border transition-colors hover:bg-[var(--vscode-bg-hover)]"
              style={{ borderColor: 'var(--vscode-border-primary)', color: 'var(--vscode-text-secondary)' }}
              title="Send email"
              aria-label="Send email"
            >
              <MessageSquare className="h-3.5 w-3.5" aria-hidden />
            </a>
          ) : null}
          <button
            type="button"
            onClick={() => copy(name)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-[8px] border transition-colors hover:bg-[var(--vscode-bg-hover)]"
            style={{ borderColor: 'var(--vscode-border-primary)', color: 'var(--vscode-text-secondary)' }}
            title="Copy name"
            aria-label="Copy name"
          >
            <Copy className="h-3.5 w-3.5" aria-hidden />
          </button>
          <button
            type="button"
            onClick={() => copy(`/candidates/${match.candidate_id}`)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-[8px] border transition-colors hover:bg-[var(--vscode-bg-hover)]"
            style={{ borderColor: 'var(--vscode-border-primary)', color: 'var(--vscode-text-secondary)' }}
            title="Share profile link"
            aria-label="Share profile link"
          >
            <Share2 className="h-3.5 w-3.5" aria-hidden />
          </button>
          <button
            type="button"
            onClick={() => onOpenFullPage(match)}
            className="ml-auto inline-flex h-8 items-center gap-1 rounded-[8px] border px-2 text-[11px] font-medium transition-colors hover:bg-[var(--vscode-bg-hover)]"
            style={{
              borderColor: 'var(--vscode-border-primary)',
              color: 'var(--vscode-text-secondary)',
            }}
          >
            Open profile
            <ExternalLink className="h-3 w-3" aria-hidden />
          </button>
        </div>

        {linkedin ? (
          <Field label="LinkedIn">
            <div className="flex items-center gap-1.5">
              <a
                href={linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="min-w-0 flex-1 truncate text-[12px] text-[color:var(--vscode-text-primary)] underline-offset-2 hover:underline"
                title={linkedin}
              >
                {linkedin.replace(/^https?:\/\//, '').replace(/\/$/, '')}
              </a>
              <button
                type="button"
                onClick={() => copy(linkedin)}
                className="shrink-0 rounded p-1 transition-colors hover:bg-[var(--vscode-bg-hover)]"
                aria-label="Copy LinkedIn"
              >
                <Copy className="h-3 w-3" style={{ color: 'var(--vscode-text-tertiary)' }} aria-hidden />
              </button>
            </div>
          </Field>
        ) : null}

        {location ? (
          <Field label="Location" Icon={MapPin}>
            {location}
          </Field>
        ) : null}

        {skills.length > 0 ? (
          <div className="mt-4">
            <p
              className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider"
              style={{ color: 'var(--vscode-text-tertiary)' }}
            >
              Skills
            </p>
            <div className="flex flex-wrap gap-1.5">
              {visibleSkills.map((s) => (
                <span
                  key={s}
                  className="inline-flex h-6 items-center rounded-full border px-2 text-[11px] font-medium"
                  style={{
                    borderColor: 'var(--vscode-border-primary)',
                    color: 'var(--vscode-text-secondary)',
                  }}
                >
                  {s}
                </span>
              ))}
              {skills.length > 10 ? (
                <button
                  type="button"
                  onClick={() => setShowAllSkills((v) => !v)}
                  className="inline-flex h-6 items-center rounded-full px-2 text-[11px] font-medium hover:bg-[var(--vscode-bg-hover)]"
                  style={{ color: 'var(--vscode-text-tertiary)' }}
                >
                  {showAllSkills ? 'Show less' : `Show ${skills.length - 10} more`}
                </button>
              ) : null}
            </div>
          </div>
        ) : null}

        {experience.length > 0 ? (
          <div className="mt-4">
            <p
              className="mb-2 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider"
              style={{ color: 'var(--vscode-text-tertiary)' }}
            >
              <Briefcase className="h-3 w-3" aria-hidden />
              Experience
            </p>
            <ul className="flex flex-col gap-3">
              {experience.slice(0, 5).map((e, i) => (
                <li key={`${e.company}-${i}`}>
                  <p
                    className="text-[12px] font-semibold"
                    style={{ color: 'var(--vscode-text-primary)' }}
                  >
                    {e.role}
                  </p>
                  <p
                    className="text-[11px]"
                    style={{ color: 'var(--vscode-text-tertiary)' }}
                  >
                    {e.company}
                    {e.period ? ` · ${e.period}` : ''}
                  </p>
                  {e.bullets && e.bullets.length > 0 ? (
                    <ul
                      className="mt-1 list-disc pl-4 text-[11px] leading-relaxed"
                      style={{ color: 'var(--vscode-text-secondary)' }}
                    >
                      {e.bullets.slice(0, 4).map((b, bi) => (
                        <li key={bi}>{b}</li>
                      ))}
                    </ul>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {education.length > 0 ? (
          <div className="mt-4">
            <p
              className="mb-2 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider"
              style={{ color: 'var(--vscode-text-tertiary)' }}
            >
              <GraduationCap className="h-3 w-3" aria-hidden />
              Education
            </p>
            <ul className="flex flex-col gap-2">
              {education.slice(0, 3).map((e, i) => (
                <li key={`${e.institution}-${i}`}>
                  <p
                    className="text-[12px] font-medium"
                    style={{ color: 'var(--vscode-text-primary)' }}
                  >
                    {e.degree}
                  </p>
                  <p
                    className="text-[11px]"
                    style={{ color: 'var(--vscode-text-tertiary)' }}
                  >
                    {e.institution}
                    {e.year ? ` · ${e.year}` : ''}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>

      <div
        className="shrink-0 border-t bg-[var(--vscode-bg-elevated)] p-3"
        style={{ borderColor: 'var(--vscode-border-primary)' }}
      >
        <div
          className="flex items-end gap-2 rounded-[10px] border px-2 py-1.5"
          style={{ borderColor: 'var(--vscode-border-primary)', background: 'var(--vscode-bg-primary)' }}
        >
          <textarea
            value={askDraft}
            onChange={(e) => setAskDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                submitAsk();
              }
            }}
            rows={1}
            placeholder="Ask about this candidate…"
            className="min-h-[28px] flex-1 resize-none bg-transparent text-[12px] leading-relaxed outline-none placeholder:text-[color:var(--vscode-text-tertiary)]"
            style={{ color: 'var(--vscode-text-primary)' }}
            aria-label="Ask about this candidate"
          />
          <button
            type="button"
            onClick={submitAsk}
            disabled={askDraft.trim().length === 0}
            className="inline-flex h-7 w-7 items-center justify-center rounded-[8px] bg-[var(--ggj-accent,#10B981)] text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            aria-label="Send"
          >
            <Send className="h-3.5 w-3.5" aria-hidden />
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  Icon,
  children,
}: {
  label: string;
  Icon?: typeof MapPin;
  children: React.ReactNode;
}) {
  return (
    <div
      className="mt-3 border-t pt-3"
      style={{ borderColor: 'var(--vscode-border-primary)' }}
    >
      <p
        className="mb-0.5 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider"
        style={{ color: 'var(--vscode-text-tertiary)' }}
      >
        {Icon ? <Icon className="h-3 w-3" aria-hidden /> : null}
        {label}
      </p>
      <div
        className="text-[12px]"
        style={{ color: 'var(--vscode-text-primary)' }}
      >
        {children}
      </div>
    </div>
  );
}
